import { useRef, useState, useMemo } from 'react'
import { handleMapToOptions } from '@/common/functions/handleMapToOptions';
import { updateInstantForm, createInstantForm } from '@/API/repositories/instantForm';
import { filterProductOptionsByMarket } from '@/common/functions/filterProductOptionsByMarket';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { ERROR_CREATE_TEXT, ERROR_UPDATE_TEXT, SUCCESS_CREATE_TEXT, SUCCESS_UPDATE_TEXT } from '../constants/index.js';

export const useHandleData = ({ edit, setShow }) => {

    const {
        options: { marketsOptions, productsOptions },
        commonData: { queues, products, markets },
    } = useCommonDataContext();

    const { makeRequest } = useRequestsContext();
    const { addMessage } = useMessageQueueContext();

    const fanpageRef = useRef();
    const formIdRef = useRef();
    const formNameRef = useRef();
    const langFieldNameRef = useRef();
    const additionalInfoRef = useRef();
    const isActiveRef = useRef();

    const [marketShort, setMarketShort] = useState(marketsOptions?.find((m) => m.value === edit?.market))
    const [productShort, setProductShort] = useState(productsOptions?.find((p) => p.value === edit?.product));
    const [countryProducts, setCountryProducts] = useState(() => filterProductOptionsByMarket(
        queues,
        productsOptions,
        marketShort?.value
    ))

    const initVariant = () => {
        const selected_variant = products.find((p) => p._id === edit.product)?.variants?.find((v) => v.short === edit.default_product_variant);
        return { label: selected_variant?.short, value: selected_variant?._id }
    }

    const initPrice = () => {
        const productData = products.find((p) => p._id === edit?.product);
        const productDataOptions = handleMapToOptions(productData?.prices, 'value', '_id');

        return productDataOptions.find((p) => parseInt(p.label) === edit?.price)
    }

    const [defaultProductVariant, setDefaultProductVariant] = useState(initVariant());

    const [price, setPrice] = useState(initPrice());

    const [additionalInfos, setAdditionalInfos] = useState(edit?.additional_info || []);

    const { prices, variants } = useMemo(() => {
        const productData = products.find((p) => p._id === productShort?.value);
        let prices = []
        let variants = []

        if (!productData) {
            return { prices, variants }
        }

        variants = handleMapToOptions(productData.variants, 'short', '_id');
        prices = handleMapToOptions(productData.prices, 'value', '_id');

        return {
            prices,
            variants,
        }
    }, [productShort])



    const handleSubmit = async (e) => {
        e && e.preventDefault();

        const isEdit = !!edit?._id;

        const formData = {
            fanpage: fanpageRef.current.value,
            form_id: formIdRef.current.value,
            form_name: formNameRef.current.value,
            product: productShort.value,
            market: marketShort.value,
            lang_field_name: langFieldNameRef.current.value,
            price: parseInt(price.label),
            default_product_variant: defaultProductVariant.label,
            additional_info: additionalInfos,
            is_active: isActiveRef.current.checked,
        };

        let response;

        if (isEdit) {
            response = await makeRequest(updateInstantForm.bind(null, edit._id, formData));
        }

        if (!isEdit) {
            response = await makeRequest(createInstantForm.bind(null, formData));
        }

        if (response?.data) {
            addMessage(!isEdit ? SUCCESS_CREATE_TEXT : SUCCESS_UPDATE_TEXT, 'success');
            setShow();
        }

        if (!response?.data) {
            addMessage(!isEdit ? ERROR_CREATE_TEXT : ERROR_UPDATE_TEXT, 'error');
        }
    };

    const handleMarketChange = (market) => {
        if (market?.value === marketShort?.value) {
            return;
        }
        setMarketShort(market)

        setCountryProducts(() => filterProductOptionsByMarket(
            queues,
            productsOptions,
            market.value
        ));

        setProductShort(null);
        setDefaultProductVariant(null);
        setPrice(null);

    }

    const handleProductChange = (product) => {
        if (product.value === productShort?.value) {
            return;
        }
        setProductShort(product)

        setDefaultProductVariant(null);
        setPrice(null);
    }

    const handleVariantChange = (variant) => {
        setDefaultProductVariant(variant);
    }

    const handlePriceChange = (price) => {
        setPrice(price);
    }

    const handleDeleteInfo = (e, _id) => {
        e && e.preventDefault();
        let filteredAdditionalInfos = additionalInfos.filter((info) => info._id !== _id);

        filteredAdditionalInfos = filteredAdditionalInfos.map((info, index) => ({
            ...info, _id: index + 1,
        }))
        setAdditionalInfos(() => filteredAdditionalInfos);
    };

    const handleAddInfo = (e) => {
        e && e.preventDefault();
        const value = additionalInfoRef.current.value;

        if (!value) {
            return;
        }

        const _id = additionalInfos.length + 1;

        setAdditionalInfos((prev) => [...prev, { _id: _id, value: value }]);
        additionalInfoRef.current.value = '';
    };



    return {
        isActiveRef,
        productsOptions,
        marketsOptions,
        products,
        markets,
        fanpageRef,
        formIdRef,
        formNameRef,
        langFieldNameRef,
        additionalInfoRef,
        marketShort,
        variants,
        defaultProductVariant, setDefaultProductVariant,
        price, setPrice,
        prices,
        additionalInfos, setAdditionalInfos,
        productShort, setProductShort,
        countryProducts, setCountryProducts,
        handleSubmit,
        handleMarketChange,
        handleProductChange,
        handleVariantChange,
        handlePriceChange,
        handleDeleteInfo,
        handleAddInfo
    }
}